// The approval matrix document as the editor changes it, and the approval
// rules a release upgrade adds as a draft (release 3.2, review H3).
//
// Its own file so the two things a reviewer has to be able to trust are
// testable without a browser: that an edit keeps every field of the document
// it was given, the ones this screen does not edit included, and that the
// words shown beside the release's draft say what it changes.
//
// # WHY THE EDITS SPREAD RATHER THAN REBUILD
//
// The editor saves the whole document it was given. A stage's roles (who may
// sign it, beyond holding the capability) and the matrix's contract waiver
// switch are not edited here, and an edit that rebuilt a stage or the matrix
// from the fields this screen knows would drop them on save: the stage would
// open to every holder of its capability and the waiver would silently stop
// counting. Every function below copies what it was given and changes one
// thing.

import type { ApprovalMatrixVersion, ApprovalStage, Capability, MatrixDocument, MatrixRow } from './api'

function sameBand(a: string, b: string): boolean {
  return a.trim().toUpperCase() === b.trim().toUpperCase()
}

export function rowFor(doc: MatrixDocument, band: string, tier: number): MatrixRow | undefined {
  return doc.rows.find((r) => r.tier === tier && sameBand(r.band, band))
}

// withAcceptance says whether reaching this band needs a written acceptance.
// Where a business draws that line is a judgement rather than a constant,
// which is why it is edited here and not in code.
export function withAcceptance(doc: MatrixDocument, band: string, tier: number, required: boolean): MatrixDocument {
  const exists = doc.rows.some((r) => r.tier === tier && sameBand(r.band, band))
  const rows = exists
    ? doc.rows.map((r) => (r.tier === tier && sameBand(r.band, band) ? { ...r, requiresAcceptance: required } : r))
    : [...doc.rows, { band, tier, stages: [], requiresAcceptance: required }]
  return { ...doc, rows }
}

export function withStages(doc: MatrixDocument, band: string, tier: number, stages: ApprovalStage[]): MatrixDocument {
  const exists = doc.rows.some((r) => r.tier === tier && sameBand(r.band, band))
  const rows = exists
    ? doc.rows.map((r) => (r.tier === tier && sameBand(r.band, band) ? { ...r, stages } : r))
    : [...doc.rows, { band, tier, stages }]
  return { ...doc, rows }
}

export function withTier(
  doc: MatrixDocument,
  tier: number,
  patch: { conditions?: string[]; why?: string },
): MatrixDocument {
  const exists = doc.conditions.some((c) => c.tier === tier)
  const conditions = exists
    ? doc.conditions.map((c) => (c.tier === tier ? { ...c, ...patch } : c))
    : [...doc.conditions, { tier, conditions: patch.conditions ?? [], why: patch.why ?? '' }]
  return { ...doc, conditions }
}

// withStageCapability changes what a stage asks for. Its roles are kept where
// they still hold the new capability and dropped where they do not, because
// the server refuses a stage naming a role that could never sign it. holders
// is who holds the new capability, by role code; null when the role table did
// not load, and then the roles are kept as they are: dropping them on an
// unknown would open the stage to everybody, and the server's check says what
// is wrong instead.
export function withStageCapability(stage: ApprovalStage, capability: Capability, holders: string[] | null): ApprovalStage {
  const next: ApprovalStage = { ...stage, capability }
  if (!stage.roles || stage.roles.length === 0 || holders === null) return next
  const kept = stage.roles.filter((r) => holders.some((h) => h.toLowerCase() === r.toLowerCase()))
  if (kept.length === 0) {
    delete next.roles
  } else {
    next.roles = kept
  }
  return next
}

// ------------------------------------------------- the release's draft

// releaseDraft is the draft the release upgrade added, if one is waiting.
export function releaseDraft(versions: ApprovalMatrixVersion[]): ApprovalMatrixVersion | undefined {
  return versions.find((v) => v.status === 'DRAFT' && v.fromRelease === true)
}

// What the release's approval rules change, in the words the banner shows.
// Plain words and no codes: the person reading it decides whether to put it in
// force, and a second person confirms on the strength of the same sentences.
export const RELEASE_RULE_HEADLINE = 'New approval rules from this release are ready to activate'

export const RELEASE_RULE_INTRO =
  'This release adds approval rules that depend on how risky a vendor is. They are a draft: nothing changes until they are activated, and activating them takes two people.'

export const RELEASE_RULE_LINES: string[] = [
  'Low risk: the TPRM team approves.',
  'Medium risk: the TPRM team, then the business owner.',
  'High risk: the TPRM team, then the Security and Risk head (the CISO or the risk officer), then the business owner.',
  'Critical risk: the same three, plus a written risk acceptance approved by the Security and Risk head and the business owner, for at most 12 months.',
  'Every vendor needs a contract on file, and a data processing agreement where it handles personal data. Where the contract cannot be filed yet, a contract waiver approved by the TPRM lead and the business owner stands in for it, for at most 90 days.',
  'Nobody approves a request they raised or sent for approval.',
]

export const RELEASE_RULE_SAFEGUARDS: string[] = [
  'Nothing changes until these rules are activated. Until then the rules in force stay exactly as they are.',
  'Vendors already waiting for approval finish under the rules they started with. Only vendors sent for approval after activation use the new rules.',
  'The TPRM Admin proposes the activation, and a second, different person confirms it.',
]

export type ReleaseStep = {
  // Which act this reader can take: the TPRM Admin's proposal, a second
  // person's confirmation, or neither.
  propose: { allowed: boolean; why: string }
  confirm: { allowed: boolean; why: string }
  // Where the draft stands, in a sentence.
  standing: string
}

// releaseStep decides which of the two buttons this reader may press, with
// the server's own rule in words: the TPRM Admin proposes; somebody who is
// not the proposer and did not change the draft confirms. The server is the
// authority and refuses the same things; this says so before the press.
export function releaseStep(
  draft: ApprovalMatrixVersion,
  reader: { subject: string; isTPRMAdmin: boolean; canApprove: boolean },
  editedBy: string[],
  shortDate: (iso: string) => string = (s) => s,
): ReleaseStep {
  const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()
  const proposer = draft.proposedBy ?? ''
  const edited = reader.subject !== '' && editedBy.some((e) => same(e, reader.subject))
  const standing = proposer
    ? 'Proposed by ' + proposer + (draft.proposedAt ? ' on ' + shortDate(draft.proposedAt) : '') +
      '. Waiting for a second person to confirm.'
    : 'Not proposed yet. The TPRM Admin proposes the activation first.'

  const propose = proposer
    ? { allowed: false, why: 'Already proposed by ' + proposer + '.' }
    : !reader.isTPRMAdmin
      ? { allowed: false, why: 'Only the TPRM Admin can propose activating these rules.' }
      : { allowed: true, why: '' }

  let confirm: { allowed: boolean; why: string }
  if (!proposer) {
    confirm = { allowed: false, why: 'The TPRM Admin has to propose the activation before anybody can confirm it.' }
  } else if (!reader.canApprove) {
    confirm = { allowed: false, why: 'Confirming needs somebody who can approve suppliers into the register.' }
  } else if (reader.subject !== '' && same(proposer, reader.subject)) {
    confirm = { allowed: false, why: 'You proposed this, so a different person has to confirm it.' }
  } else if (edited) {
    confirm = { allowed: false, why: 'You changed this draft, so a different person has to confirm it.' }
  } else {
    confirm = { allowed: true, why: '' }
  }
  return { propose, confirm, standing }
}
